import React, { useState } from 'react';
import { Video, Check, Users, MessageSquareText, Globe, Zap, Shield, Crown, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentWallPage = ({ user }) => {
  const [loading, setLoading] = useState(null); // 'basic' or 'pro'

  const handleSubscribe = async (planType) => {
    setLoading(planType);
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.post(`${API}/stripe/create-checkout`, {
        origin_url: window.location.origin,
        plan_type: planType
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error('Checkout error:', e);
      toast.error('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const sharedFeatures = [
    { icon: Video, text: 'Host video calls with live captions' },
    { icon: MessageSquareText, text: 'Save conversations with AI summaries' },
    { icon: Globe, text: 'Real-time translation in 10 languages' },
    { icon: Zap, text: 'Cinematic Netflix-style captions' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white font-inter flex items-center justify-center p-6 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#D4AF37]/[0.06] rounded-full blur-[180px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-violet-900/[0.08] rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-4xl w-full py-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 mb-6">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]">Host Membership</span>
          </div>
          <h1 className="font-satoshi font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none mb-4">
            Choose your <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5E6C8] bg-clip-text text-transparent">Host plan</span>
          </h1>
          <p className="text-zinc-400 text-base max-w-md mx-auto">
            Only the host pays. Guests always join free.
          </p>
          <div className="inline-block bg-[#D4AF37]/[0.08] text-[#D4AF37] text-xs font-semibold tracking-wider uppercase px-4 py-2 rounded-full mt-5 border border-[#D4AF37]/20">
            3-day free trial · No credit card to try
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Basic Plan */}
          <div className="backdrop-blur-2xl bg-black/60 border border-white/[0.08] rounded-3xl p-8 flex flex-col hover:border-white/[0.15] transition-all duration-300" data-testid="basic-plan-card">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center">
                  <Shield className="w-4 h-4 text-zinc-300" />
                </div>
                <h2 className="font-satoshi font-bold text-lg">Basic Host</h2>
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-satoshi font-black text-5xl tracking-tighter">$9.99</span>
                <span className="text-zinc-500 text-sm">/month</span>
              </div>
              <p className="text-sm text-zinc-500">Perfect for personal calls</p>
            </div>

            <div className="space-y-3.5 mb-7 flex-1">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-white/[0.06] rounded-md flex items-center justify-center flex-shrink-0">
                  <Users className="w-3 h-3 text-zinc-300" />
                </div>
                <span className="text-sm text-white font-medium">1 Guest per call</span>
              </div>
              {sharedFeatures.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-white/[0.03] rounded-md flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-zinc-500" />
                  </div>
                  <span className="text-sm text-zinc-400">{f.text}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => handleSubscribe('basic')}
              disabled={loading !== null}
              data-testid="subscribe-basic"
              className="w-full py-3.5 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] rounded-full font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading === 'basic' ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>Start free trial <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>

          {/* Pro Plan */}
          <div className="relative backdrop-blur-2xl bg-gradient-to-br from-[#D4AF37]/[0.08] via-black/60 to-black/60 border border-[#D4AF37]/30 rounded-3xl p-8 flex flex-col hover:shadow-[0_0_50px_rgba(212,175,55,0.1)] transition-all duration-300" data-testid="pro-plan-card">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black text-[10px] font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full shadow-[0_0_20px_rgba(212,175,55,0.4)]">
              Most Popular
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.2)]">
                  <Crown className="w-4 h-4 text-black" />
                </div>
                <h2 className="font-satoshi font-bold text-lg">Pro Host</h2>
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-satoshi font-black text-5xl tracking-tighter bg-gradient-to-r from-[#D4AF37] to-[#F5E6C8] bg-clip-text text-transparent">$17.99</span>
                <span className="text-zinc-500 text-sm">/month</span>
              </div>
              <p className="text-sm text-zinc-400">Perfect for teams & group calls</p>
            </div>

            <div className="space-y-3.5 mb-7 flex-1">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-[#D4AF37]/15 rounded-md flex items-center justify-center flex-shrink-0">
                  <Users className="w-3 h-3 text-[#D4AF37]" />
                </div>
                <span className="text-sm text-white font-semibold">Up to 5 Guests per call</span>
              </div>
              {sharedFeatures.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#D4AF37]/10 rounded-md flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-[#D4AF37]" />
                  </div>
                  <span className="text-sm text-zinc-300">{f.text}</span>
                </div>
              ))}
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-[#D4AF37]/15 rounded-md flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-[#D4AF37]" />
                </div>
                <span className="text-sm text-zinc-300">Group call capability</span>
              </div>
            </div>

            <button
              onClick={() => handleSubscribe('pro')}
              disabled={loading !== null}
              data-testid="subscribe-pro"
              className="w-full py-3.5 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-bold rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading === 'pro' ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  <Crown className="w-4 h-4" />
                  Start free trial
                </>
              )}
            </button>
          </div>
        </div>

        <p className="text-center text-zinc-600 text-xs mt-10 tracking-wide">
          Cancel anytime · No commitment · Secured by Stripe
        </p>
      </div>
    </div>
  );
};

export default PaymentWallPage;
